/**
 * MissionListView -- Interactive mission list with create and delete.
 *
 * @module presentation/views/MissionListView
 */

import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { leerCierre } from '../../application/misiones/cierreDeMision.js';
import { avisarCierre } from '../shared/avisoDeCierre.js';
import { avisar, confirmar } from '../shared/nxDialogo.js';
import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';
import { NotesApiClient } from '../../infrastructure/api/NotesApiClient.js';
import { getJwtUserId } from '../shared/jwtUserId.js';
import { PRIORITY_RANK, PRIORITY_STYLE, MISSION_PRIORITIES } from '../../domain/entities/Mission.js';

// Deuda 4: el escape local no cambiaba & ni " y perdia el 0; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

function parseMissionMetadata(m) {
  let meta = m.metadata ?? m.Metadata;
  if (meta == null) return {};
  if (typeof meta === 'string') {
    try { return JSON.parse(meta); } catch { return {}; }
  }
  return typeof meta === 'object' ? meta : {};
}

// por_cerrar (MISIONES-CANDADOS-T1, A #33027): la base la pone sola cuando cierra la ultima tarea abierta;
// espera el [VERIFICADO] de Ares. Sin rotulo ni filtro propio desapareceria de la vista.
const STATUS_COLORS = {
  in_progress: '#58a6ff', active: '#238636', por_cerrar: '#f0883e', draft: '#d29922',
  completed: '#bc8cff', archived: '#6e7681'
};

const STATUS_LABELS = {
  in_progress: 'en progreso', active: 'activa', por_cerrar: 'por cerrar', draft: 'borrador',
  completed: 'completada', archived: 'archivada'
};

const MISSION_STATUSES = ['in_progress', 'active', 'por_cerrar', 'draft', 'completed', 'archived'];

const STATUS_SORT_ORDER = { in_progress: 0, active: 1, por_cerrar: 2, draft: 3, completed: 4, archived: 5 };

let currentMissionFilter = 'in_progress';
let currentPriorityFilter = 'all';

function badge(status) {
  const color = STATUS_COLORS[status] || '#6e7681';
  const label = STATUS_LABELS[status] || status;
  return `<span class="status-badge" style="background:${color}22;color:${color}">${label}</span>`;
}

function priorityBadge(priority) {
  const p = (priority || 'medium').toLowerCase();
  const style = PRIORITY_STYLE[p] || PRIORITY_STYLE.medium;
  return `<span class="priority-badge priority-${p}" title="Prioridad ${style.label}"
    style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:4px;
    font-size:0.875rem;font-weight:700;letter-spacing:0.05em;
    background:${style.bg};color:${style.color};border:1px solid ${style.border}">
    ${p === 'critical' ? '⚠ ' : ''}${style.label}
  </span>`;
}

function missionStatusOptions(current) {
  return MISSION_STATUSES
    .filter(s => s !== current)
    .map(s => `<option value="${s}">${STATUS_LABELS[s]}</option>`)
    .join('');
}

function extractAgents(desc) {
  if (!desc) return [];
  const m = desc.match(/\[AGENTE:\s*([^\]]+)\]/i);
  if (!m) return [];
  return m[1].split(',').map(a => a.trim()).filter(Boolean);
}

// PANEL-MISIONES-UX-V1-001: los codigos de agente son largos (ARGY-PANEL-LOCAL-001) y
// desbordaban la fila. Acortamos a etiqueta legible conservando el codigo completo en title.
function shortAgent(code) {
  const c = String(code || '').toUpperCase().trim();
  const known = {
    'ARGY-PANEL-LOCAL-001': 'PANEL', 'ARGY-DESKTOP-LOCAL-001': 'DESKTOP',
    'ARGY-API-LOCAL-001': 'API', 'ARGY-Q-LOCAL-001': 'Q', 'ARGY-W-LOCAL-001': 'W',
    'ARES-001': 'ARES', 'ARGYS-001': 'ARGYS', 'ARGEMIRO-001': 'ARGEMIRO',
    'ARGEMIRO-CLIENTE-001': 'ARGEMIRO', 'AGT-WIZARD-000': 'JOSSE'
  };
  if (known[c]) return known[c];
  // Fallback generico: quitar prefijo ARGY-, sufijo -LOCAL-00N o -00N.
  return c.replace(/^ARGY-/, '').replace(/-LOCAL-\d+$/, '').replace(/-\d+$/, '') || c;
}

// Renderiza las etiquetas de agente capadas (max 2 visibles) + chip "+K" con el resto en title.
function renderAgentTags(agents, max = 2) {
  if (!agents.length) return '';
  const vis = agents.slice(0, max);
  const rest = agents.slice(max);
  const tags = vis.map(a =>
    `<span class="agent-tag" title="${a}">${shortAgent(a)}</span>`).join('');
  const overflow = rest.length
    ? `<span class="agent-tag agent-tag-more" title="${rest.join(', ')}">+${rest.length}</span>`
    : '';
  return tags + overflow;
}

function missionSkillProgressStrip(m, progressByCode) {
  const meta = parseMissionMetadata(m);
  const skCode = meta.skill_code;
  if (!skCode || !progressByCode?.has(skCode)) return '';
  const s = progressByCode.get(skCode);
  const answered = parseInt(s.answered, 10) || 0;
  const accuracy = parseFloat(s.accuracy) || 0;
  const required = parseInt(s.questions_required, 10) || 10;
  const reqAccuracy = parseInt(s.accuracy_required, 10) || 70;
  const progressPct = Math.min(100, Math.round((answered / required) * 100));
  const unlocked = answered >= required && accuracy >= reqAccuracy;
  const barColor = unlocked ? '#238636' : '#58a6ff';
  return `
        <div class="mission-skill-progress" style="margin-top:8px;padding:8px;background:#161b22;border-radius:6px;border-left:3px solid ${barColor}">
          <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">
            Skill <code style="font-size:0.875rem">${escHtml(skCode)}</code>
            ${s.name ? ` · ${escHtml(s.name)}` : ''}
          </div>
          <div class="progress-bar" style="height:5px;background:#21262d;border-radius:3px;overflow:hidden">
            <div style="width:${progressPct}%;height:100%;background:${barColor};border-radius:3px;transition:width 0.25s"></div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:3px;font-size:0.875rem;color:#6e7681">
            <span>${progressPct}%</span>
            <span>${answered}/${required} preg. · ${accuracy}% prec.</span>
          </div>
        </div>`;
}

function missionRow(m, progressByCode) {
  const reward = m.rewardNexos ? `${m.rewardNexos} NXS` : '';
  const taskCount = m.taskIds?.length || 0;
  const instCount = m.instructionCodes?.length || 0;
  const isWorking = m.status === 'in_progress';
  const priority = (m.priority || 'medium').toLowerCase();
  const isCritical = priority === 'critical';
  const agents = extractAgents(m.description);
  const skillStrip = missionSkillProgressStrip(m, progressByCode);
  const criticalBorder = isCritical ? 'border-left:3px solid #ff2d55;background:rgba(255,45,85,0.04)' : '';
  return `
    <div class="artifact-item mission-row ${isWorking ? 'mission-working' : ''}" data-id="${m.id}" style="${criticalBorder}">
      <a href="#/missions/${m.id}" class="task-info mission-link">
        <div class="artifact-item-title">
          ${isWorking ? '<span class="working-indicator"></span>' : ''}
          ${priorityBadge(priority)}
          <code class="mission-code">${m.code || ''}</code> ${m.title || '(sin titulo)'}
        </div>
        <div class="artifact-item-meta">
          ${m.description?.substring(0, 80) || 'Sin descripcion'}
          ${taskCount > 0 ? ` &middot; ${taskCount} tareas` : ''}
          ${instCount > 0 ? ` &middot; ${instCount} instrucciones` : ''}
        </div>
        ${skillStrip}
      </a>
      <div class="task-actions">
        ${renderAgentTags(agents)}
        ${reward ? `<span class="artifact-item-meta">${reward}</span>` : ''}
        ${badge(m.status)}
        <select class="priority-select" data-mission-priority="${m.id}" title="Cambiar prioridad"
          style="padding:2px 6px;font-size:0.875rem;background:transparent;border:1px solid #30363d;border-radius:4px;color:#c9d1d9">
          <option value="" disabled selected>⚑</option>
          ${MISSION_PRIORITIES.filter(p => p !== priority).map(p => `<option value="${p}">${p}</option>`).join('')}
        </select>
        <select class="status-select" data-mission-status="${m.id}" title="Cambiar estado">
          <option value="" disabled selected>Cambiar</option>
          ${missionStatusOptions(m.status)}
        </select>
        <button class="btn-icon btn-delete" data-id="${m.id}" title="Eliminar">&times;</button>
      </div>
    </div>`;
}

export async function renderMissionList(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Misiones</h1>
        </div>
        <button class="btn-primary btn-sm" id="btn-new-mission">+ Nueva mision</button>
      </div>

      <div class="task-filter-bar" id="nx-mission-filters">
        <button class="btn-filter active" data-filter="in_progress">En progreso</button>
        <button class="btn-filter" data-filter="active">Activas</button>
        <button class="btn-filter" data-filter="por_cerrar">Por cerrar</button>
        <button class="btn-filter" data-filter="all">Todas</button>
        <button class="btn-filter" data-filter="completed">Completadas</button>
        <button class="btn-filter" data-filter="archived">Archivadas</button>
      </div>

      <div class="task-filter-bar" id="nx-priority-filters" style="margin-top:8px">
        <span style="font-size:0.875rem;color:#8b949e;padding:4px 8px">Prioridad:</span>
        <button class="btn-filter active" data-priority="all">Todas</button>
        <button class="btn-filter" data-priority="critical" style="color:#ff2d55">⚠ Critical</button>
        <button class="btn-filter" data-priority="high" style="color:#ff9f0a">High</button>
        <button class="btn-filter" data-priority="medium">Medium</button>
        <button class="btn-filter" data-priority="low" style="color:#0a84ff">Low</button>
      </div>

      <div id="nx-mission-form-area" hidden>
        <form id="nx-mission-form" class="inline-form inline-form-col">
          <div class="form-row">
            <input type="text" id="nx-mission-code" placeholder="Codigo (ej: MISSION-001)" required class="form-input form-input-sm">
            <input type="text" id="nx-mission-title" placeholder="Titulo" required class="form-input">
          </div>
          <div class="form-row">
            <input type="text" id="nx-mission-desc" placeholder="Descripcion (opcional)" class="form-input">
            <input type="number" id="nx-mission-nxs" placeholder="NXS" min="0" value="10" class="form-input" style="max-width:80px">
            <select id="nx-mission-priority" class="form-input form-input-sm" style="max-width:140px" title="Prioridad">
              <option value="medium" selected>Medium</option>
              <option value="high">High</option>
              <option value="low">Low</option>
              <option value="critical">⚠ Critical</option>
            </select>
            <select id="nx-mission-agent" class="form-input form-input-sm" style="max-width:160px">
              <option value="">Sin agente</option>
              <option value="ARGEMIRO-001">Argemiro</option>
              <option value="ARGYS-001">Argys</option>
              <option value="ARES-001">Ares</option>
            </select>
          </div>
          <div class="form-row">
            <button type="submit" class="btn-primary btn-sm">Crear</button>
            <button type="button" class="btn-ghost btn-sm" id="btn-cancel-mission">Cancelar</button>
          </div>
        </form>
        <p id="nx-mission-form-error" class="auth-error" hidden></p>
      </div>

      <div class="artifact-list" id="nx-mission-list">
        <p class="empty-state">Cargando misiones...</p>
      </div>
    </section>`;

  setupFormHandlers();
  setupMissionFilterHandlers();
  setupListHandlers();
  consumeNotePrefillIfPresent(container);
  await loadMissions();
}

/**
 * Si hay un payload guardado por NotesView (boton Convertir-a-mision), abre
 * el form-area y pre-rellena los inputs. Consume sessionStorage 1 vez para
 * evitar re-aplicar tras refresh manual. Scope al container del render actual
 * para evitar rellenar formularios duplicados (sandbox tests, multi-render).
 */
function consumeNotePrefillIfPresent(container) {
  const raw = sessionStorage.getItem('nx_mission_prefill');
  if (!raw) return;
  let prefill = null;
  try { prefill = JSON.parse(raw); } catch {
    sessionStorage.removeItem('nx_mission_prefill');
    return;
  }
  if (!prefill || !prefill.title) {
    sessionStorage.removeItem('nx_mission_prefill');
    return;
  }

  const scope = container || document;
  const $area = scope.querySelector('#nx-mission-form-area');
  const $code = scope.querySelector('#nx-mission-code');
  const $title = scope.querySelector('#nx-mission-title');
  const $desc = scope.querySelector('#nx-mission-desc');
  const $prio = scope.querySelector('#nx-mission-priority');
  // Si el form aun no esta en el DOM (sandbox / render inicial incompleto),
  // dejar el prefill en sessionStorage para que el siguiente paso lo consuma.
  if (!$area || !$code || !$title) return;
  // Form OK -> consumir el prefill ahora
  sessionStorage.removeItem('nx_mission_prefill');

  $area.hidden = false;
  $code.value = prefill.suggestedCode || '';
  $title.value = prefill.title || '';
  if ($desc) $desc.value = prefill.description || '';
  if ($prio && prefill.priority) $prio.value = prefill.priority;
  // Guardar noteId + metadata existente en dataset del form para merge tras crear
  const $form = scope.querySelector('#nx-mission-form');
  if ($form && prefill.noteId) {
    $form.dataset.originNoteId = prefill.noteId;
    if (prefill.existingMetadata) {
      $form.dataset.originNoteMetadata = JSON.stringify(prefill.existingMetadata);
    }
  }
  setTimeout(() => $code.focus(), 50);
}

function setupMissionFilterHandlers() {
  const $bar = document.getElementById('nx-mission-filters');
  $bar.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    $bar.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentMissionFilter = btn.dataset.filter;
    loadMissions();
  });

  const $prio = document.getElementById('nx-priority-filters');
  if ($prio) {
    $prio.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn-filter');
      if (!btn) return;
      $prio.querySelectorAll('.btn-filter').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      currentPriorityFilter = btn.dataset.priority;
      loadMissions();
    });
  }
}

function setupFormHandlers() {
  const $btn = document.getElementById('btn-new-mission');
  const $area = document.getElementById('nx-mission-form-area');
  const $form = document.getElementById('nx-mission-form');
  const $cancel = document.getElementById('btn-cancel-mission');
  const $error = document.getElementById('nx-mission-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-mission-code').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    const code = document.getElementById('nx-mission-code').value.trim();
    const title = document.getElementById('nx-mission-title').value.trim();
    const description = document.getElementById('nx-mission-desc').value.trim();

    if (!code || !title) {
      $error.textContent = 'Codigo y titulo son requeridos';
      $error.hidden = false;
      return;
    }

    const rewardNexos = parseInt(document.getElementById('nx-mission-nxs').value, 10) || 0;
    const priority = document.getElementById('nx-mission-priority')?.value || 'medium';
    const agentCode = document.getElementById('nx-mission-agent')?.value || '';
    const fullDesc = agentCode
      ? `[AGENTE: ${agentCode}] ${description}`.trim()
      : description;
    const result = await MissionApiClient.create({
      code, title, description: fullDesc, rewardNexos, status: 'active', priority
    });

    if (result.ok) {
      // Si la mision viene de una nota (boton Convertir-a-mision), vincular bidireccional:
      // PATCH la nota agregando metadata.converted_to_mission_id/code para que la nota
      // muestre chip "Convertida a mision X" al volver a #/notas.
      const originNoteId = $form.dataset.originNoteId;
      if (originNoteId) {
        try {
          const created = result.data?.data ?? result.data;
          const newMissionId = created?.id;
          if (newMissionId) {
            // Merge con metadata existente: PATCH sobrescribe el campo entero,
            // hay que preservar campos previos (ej: related_artefact).
            let prevMeta = {};
            try { prevMeta = JSON.parse($form.dataset.originNoteMetadata || '{}'); } catch {}
            const mergedMetadata = {
              ...prevMeta,
              converted_to_mission_id: newMissionId,
              converted_to_mission_code: code,
              converted_at: new Date().toISOString()
            };
            await NotesApiClient.update(originNoteId, { metadata: mergedMetadata });
          }
        } catch (linkErr) {
          console.warn('[MissionListView] no se pudo vincular nota -> mision', linkErr);
        }
        delete $form.dataset.originNoteId;
        delete $form.dataset.originNoteMetadata;
      }
      $area.hidden = true;
      $form.reset();
      await loadMissions();
    } else {
      $error.textContent = result.error;
      $error.hidden = false;
    }
  };
}

function setupListHandlers() {
  const $list = document.getElementById('nx-mission-list');

  $list.addEventListener('change', async (e) => {
    const statusSelect = e.target.closest('[data-mission-status]');
    const prioritySelect = e.target.closest('[data-mission-priority]');
    if (statusSelect) {
      const id = statusSelect.dataset.missionStatus;
      const newStatus = statusSelect.value;
      if (!id || !newStatus) return;

      statusSelect.disabled = true;
      const result = await MissionApiClient.update(id, { status: newStatus });
      if (result.ok) {
        await loadMissions();
      } else {
        // Cerrar desde la lista tambien paga (PR 435): un no admin recibe 403 SOLO_ADMIN_CIERRA y se dice con la misma
        // frase que en el detalle. Toast en vez de alert() (prohibido en el panel) y la lista se relee para que el
        // selector vuelva al estado real.
        const cierre = newStatus === 'completed' ? leerCierre(result) : { frase: `Error: ${result.error}`, motivo: 'ERROR' };
        avisarCierre(cierre.frase, cierre.motivo);
        await loadMissions();
      }
      return;
    }
    if (prioritySelect) {
      const id = prioritySelect.dataset.missionPriority;
      const newPriority = prioritySelect.value;
      if (!id || !newPriority) return;
      prioritySelect.disabled = true;
      const result = await MissionApiClient.update(id, { priority: newPriority });
      if (result.ok) {
        await loadMissions();
      } else {
        avisar(`Error: ${result.error}`, { tono: 'error' });
        prioritySelect.disabled = false;
      }
    }
  });

  $list.addEventListener('click', async (e) => {
    const btn = e.target.closest('.btn-delete');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const id = btn.dataset.id;
    if (!await confirmar({ titulo: 'Eliminar esta mision', texto: 'No se puede deshacer.', aceptar: 'Eliminar mision', peligro: true })) return;

    btn.disabled = true;
    const result = await MissionApiClient.remove(id);
    if (result.ok) {
      await loadMissions();
    } else {
      avisar(`Error: ${result.error}`, { tono: 'error' });
      btn.disabled = false;
    }
  });
}

/**
 * TODAS LAS MISIONES DEL FILTRO, no la primera pagina.
 *
 * EL DEFECTO QUE ARREGLA NO ERA UN NUMERO MAL PUESTO: la pantalla pedia UNA
 * pagina de 50 y pintaba items.length. Con el filtro Completadas hay 184
 * misiones: decia 50 y las otras 134 no aparecian en ninguna parte, porque la
 * pagina 2 no la pedia nadie. El conteo mentia y la lista tambien.
 *
 * Y ES LA SEGUNDA VEZ QUE ESTE MISMO DEFECTO MUERDE EN ESTA PANTALLA. En
 * main.js, tres funciones mas arriba, hay un comentario que dice "mostraba 24
 * en vez de 73" y su arreglo fue exactamente este: usar totalCount. La receta
 * ya estaba en el archivo de al lado.
 *
 * EL TOPE EXISTE Y SE DECLARA: veinte paginas de cien. Si un dia hay mas de dos
 * mil misiones de un mismo estado, esto trae dos mil y DICE que no las trajo
 * todas, en vez de fingir un total redondo. Un freno mudo seria volver al mismo
 * problema con otro numero.
 */
const MISIONES_POR_PAGINA = 100;
const TOPE_DE_PAGINAS = 20;

async function traerTodasLasMisiones(apiQuery) {
  const primera = await MissionApiClient.list(1, MISIONES_POR_PAGINA, apiQuery);
  if (!primera.ok) return { ok: false, error: primera.error };

  const p1 = primera.data?.data || primera.data;
  let items = [...(p1?.items || [])];
  // El total lo dice el SERVIDOR. Si no lo mandara, lo unico honesto es contar
  // lo que llego y decir que ese es el tope de lo que sabemos.
  const totalServidor = Number.isFinite(Number(p1?.totalCount)) ? Number(p1.totalCount) : items.length;

  let pagina = 1;
  while (items.length < totalServidor && pagina < TOPE_DE_PAGINAS) {
    pagina++;
    const r = await MissionApiClient.list(pagina, MISIONES_POR_PAGINA, apiQuery);
    if (!r.ok) break;
    const p = r.data?.data || r.data;
    const lote = p?.items || [];
    // Sin lote, el servidor no tiene mas que dar: insistir seria un bucle.
    if (!lote.length) break;
    items = items.concat(lote);
  }

  return { ok: true, items, totalServidor, completo: items.length >= totalServidor };
}

async function loadMissions() {
  const $list = document.getElementById('nx-mission-list');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';

  const uid = getJwtUserId();
  // Filtro server-side: evita que con muchas misiones (50+) las de in_progress
  // queden fuera del pageSize. Solo 'all' trae todo.
  const apiQuery = currentMissionFilter === 'all' ? {} : { status: currentMissionFilter };
  const [result, progressRes] = await Promise.all([
    traerTodasLasMisiones(apiQuery),
    LearningApiClient.getSkillProgress(uid !== 'anon' ? uid : undefined)
  ]);

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  const progressByCode = new Map();
  if (progressRes.ok) {
    for (const s of (progressRes.data?.data || [])) {
      if (s.code) progressByCode.set(s.code, s);
    }
  }

  let items = [...result.items].sort((a, b) => {
    // 1) priority DESC (critical primero)
    const pa = PRIORITY_RANK[(a.priority || 'medium').toLowerCase()] ?? 3;
    const pb = PRIORITY_RANK[(b.priority || 'medium').toLowerCase()] ?? 3;
    if (pa !== pb) return pa - pb;
    // 2) status (in_progress primero)
    const sa = STATUS_SORT_ORDER[a.status] ?? 9;
    const sb = STATUS_SORT_ORDER[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    // 3) updatedAt DESC
    return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
  });

  if (currentMissionFilter !== 'all') {
    items = items.filter(m => m.status === currentMissionFilter);
  }
  if (currentPriorityFilter !== 'all') {
    items = items.filter(m => (m.priority || 'medium').toLowerCase() === currentPriorityFilter);
  }

  const filterLabel = currentMissionFilter === 'all'
    ? '' : ` (${STATUS_LABELS[currentMissionFilter] || currentMissionFilter})`;
  const prioLabel = currentPriorityFilter === 'all' ? '' : ` [${currentPriorityFilter}]`;

  if (items.length === 0) {
    $list.innerHTML = `<p class="empty-state">No hay misiones${filterLabel}${prioLabel}</p>`;
    return;
  }

  // EL NUMERO SALE DEL SERVIDOR, NO DEL LARGO DE LA PAGINA.
  //
  // Con filtro de prioridad, el total del servidor NO sirve: el servidor conto
  // por estado y la prioridad se cruza aqui. En ese caso el numero bueno es lo
  // que quedo despues de filtrar -- y ahora vale, porque se filtra sobre TODAS
  // las misiones del estado y no sobre las primeras cincuenta.
  const conteo = currentPriorityFilter === 'all' ? result.totalServidor : items.length;
  // Y SI NO SE PUDO TRAER TODO, SE DICE, Y CON LAS PALABRAS DE LA CASA.
  // El guardia de lineamientos del build me rechazo mi primera redaccion -- -no
  // alcance a traerlas todas- -- porque no estaba en su vocabulario. Tiene razon
  // el guardia y no lo amplie: si cada vista avisa con su propia frase, la regla
  // deja de poder comprobarse. Se dice como lo dice el resto del panel. Un total redondo sin poder mostrarlo
  // es la misma mentira de antes con otra cara.
  const aviso = result.completo
    ? ''
    : ` &mdash; se muestran ${items.length}: se corto por el tope y puede haber mas`;
  const countLine = `<div class="mission-list-count">${conteo} mision${conteo === 1 ? '' : 'es'}${filterLabel}${prioLabel}${aviso}</div>`;
  $list.innerHTML = countLine + items.map(m => missionRow(m, progressByCode)).join('');
}
